import styled from 'styled-components'
import { useEffect, useState } from 'react'

import Badge from 'productTemplate/components/Badge'
import Divider from 'shared/components/Divider'
import Image from 'shared/components/Image'
import RadioButton from 'checkout/components/RadioButton'
import RadioGroup from 'checkout/components/RadioGroup'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { AccountQA, CheckoutQA } from 'shared/dataAttributes'
import { ApplicableCostsModal } from 'checkout/panels/OrderSummary/ApplicableCostsModal'
import { PriceBreakdownLineItemType } from 'shared/enums/PriceBreakdownLineItemType'
import { RotatingCaret } from 'shared/components'
import { colors, fonts } from 'shared/lib'
import { setIsFastestToYouAvailable } from 'store/checkoutSlice'
import { t } from 'localization'
import { useAppDispatch } from 'store/hooks'
import { useCheckoutContext } from 'checkout/containers/CheckoutContextProvider'
import { useGetPricesQuery } from 'api/productVariantsApi'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { useElementOnScreen } from 'shared/hooks/useElementOnScreen'

const TP = 'checkout.panels.OrderSummary.OrderSummaryOptionsMobile'

type OrderOptions = 'fastestToYou' | 'lowestPrice'

export const OrderSummaryOptionsMobile = () => {
  const { changeOrderOptionRTK, order, panels } = useCheckoutContext()
  const { country } = useShoppingRegionContext()
  const [containerRef, isVisible] = useElementOnScreen({ threshold: 1.0 })
  const dispatch = useAppDispatch()
  const [displayLineItems, setDisplayLineItems] = useState(false)
  const [isApplicableCostsModalOpen, setIsApplicableCostsModalOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<OrderOptions>(
    order?.isInstantShip ? 'fastestToYou' : 'lowestPrice',
  )

  const { data: prices, isSuccess } = useGetPricesQuery({
    countryCode: country,
    productTemplateId: order?.product?.productTemplate?.id,
    size: order?.product?.sizeOption?.presentation,
  })

  const orderSubtotal = order?.pricingBreakdown?.lineItems?.find(
    (lineItem) => lineItem?.type === PriceBreakdownLineItemType.SUBTOTAL,
  )

  const handleClickNote = () => {
    setIsApplicableCostsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsApplicableCostsModalOpen(false)
  }

  const handleClickOption = async (option: OrderOptions) => {
    setSelectedOption(option)
    const isFty = option === 'fastestToYou'
    const priceCents = isFty
      ? prices?.[0]?.instantShipLowestPriceCents?.amount
      : prices?.[0]?.lowestPriceCents?.amount
    const orderDetails = {
      expectedPriceCents: priceCents,
      instantShip: isFty,
    }
    sendTrackingEvent(isFty ? 'FASTEST_TO_YOU_SELECTION_TAP' : 'LOWEST_PRICE_SELECTION_TAP', {
      fastest_price: `${prices?.[0]?.instantShipLowestPriceCents?.amount}`,
      lowest_price: `${prices?.[0]?.lowestPriceCents?.amount}`,
      order_number: `${order?.number || null}`,
      product_id: `${order?.product?.id || null}`,
      product_template_id: `${order?.product?.productTemplate?.id || null}`,
      size: order?.product?.sizeOption?.presentation,
    })
    await changeOrderOptionRTK(orderDetails)
  }

  useEffect(() => {
    if (!isVisible) {
      setDisplayLineItems(false)
    }
  }, [isVisible])

  useEffect(() => {
    if (isSuccess) {
      sendTrackingEvent('FASTEST_TO_YOU_VIEW', {
        fastest_price: `${prices?.[0]?.instantShipLowestPriceCents?.amount}`,
        lowest_price: `${prices?.[0]?.lowestPriceCents?.amount}`,
        order_number: `${order?.number || null}`,
        product_id: `${order?.product?.id || null}`,
        product_template_id: `${order?.product?.productTemplate?.id || null}`,
        size: order?.product?.sizeOption?.presentation,
      })
      dispatch(setIsFastestToYouAvailable(!!prices?.[0]?.instantShipLowestPriceCents?.amount))
    }
  }, [isSuccess])

  if (
    panels?.guestCheckoutAuth?.isEditing ||
    (isSuccess && !prices?.[0]?.instantShipLowestPriceCents?.amount)
  ) {
    return null
  }

  return (
    <Container $displayLineItems={displayLineItems}>
      <ProductSummaryContainer>
        <StyledImage
          alt={order?.product?.productTemplate?.name}
          data-qa={CheckoutQA.OrderSummaryShoeImage}
          height={77}
          src={order?.product?.mainPictureUrl}
          width={77}
        />
        <ProductDescription>
          <ProductName data-qa={CheckoutQA.OrderSummaryShoeName}>
            {order?.product?.productTemplate?.name}
          </ProductName>
          {order?.product?.sizeOption?.presentation && (
            <Flex>
              <Size data-qa={CheckoutQA.OrderSummaryShoeSize}>
                {t(`${TP}.productSize`, {
                  defaultValue: 'US Size {size}',
                  size: order?.product?.sizeOption?.presentation,
                })}
              </Size>
            </Flex>
          )}
          <ProductDetailsLineTwo>
            <Flex>
              <ProductDetails data-qa={CheckoutQA.OrderSummaryShoeColorway}>
                {order?.product?.productTemplate?.details}
              </ProductDetails>
            </Flex>
          </ProductDetailsLineTwo>
        </ProductDescription>
      </ProductSummaryContainer>
      <RadioOptions name="instantShipOptions">
        <Option
          checked={selectedOption === 'lowestPrice'}
          key="lowestPrice"
          onRadioSelect={() => handleClickOption('lowestPrice')}
          qaAttr="lowest-price-radio-option"
          subtitle={t(`${TP}.lowestPriceDelivery`, 'Ships to us first for verification')}
          title={<Badge text={t(`${TP}.lowestPriceBadge`, 'Lowest Price')} badgeType="primary1" />}
          value="lowestPrice"
        />
        <Option
          checked={selectedOption === 'fastestToYou'}
          key="fastestToYou"
          onRadioSelect={() => handleClickOption('fastestToYou')}
          qaAttr="fastest-to-you-radio-option"
          subtitle={t(`${TP}.fastestToYouDelivery`, 'Estimated arrival in 1-5 business days')}
          title={
            <Badge text={t(`${TP}.fastestToYouBadge`, 'Fastest to you')} badgeType="primary2" />
          }
          value="fastestToYou"
        />
      </RadioOptions>
      <MarginDivider />
      <StickyOrderSummaryObserver ref={containerRef} />
      <StickyOrderSummaryContainer
        className={isVisible ? '' : 'sticky-summary'}
        id="sticky-order-summary"
      >
        <LineItem onClick={() => setDisplayLineItems(!displayLineItems)}>
          <LineItemText data-qa={AccountQA.OrderTotalTag}>
            {t(`${TP}.orderSummary`, 'Order Summary')}
          </LineItemText>
          <LineItemValue data-qa={AccountQA.OrderTotalAmount}>
            {order?.pricingBreakdown?.total?.formattedAmount}
            <span data-qa={CheckoutQA.OrderSummaryBreakdownToggle}>
              <Caret color={colors.FC2_GREY} isOpen={displayLineItems} />
            </span>
          </LineItemValue>
        </LineItem>
        {order?.pricingBreakdown?.lineItems?.map((lineItem) => {
          if (
            lineItem.type === PriceBreakdownLineItemType.APPLICABLE_COSTS &&
            lineItem?.localizedAmount?.amount === 0
          ) {
            return null
          }
          return (
            displayLineItems && (
              <>
                <PriceLine
                  $isSubtotal={lineItem?.type === PriceBreakdownLineItemType.SUBTOTAL}
                  key={lineItem?.type}
                >
                  {lineItem?.type === PriceBreakdownLineItemType.APPLICABLE_COSTS &&
                  order?.applicableCostsNote ? (
                    <DetailTitleButton
                      data-qa={getDataQaLabel(lineItem?.type)}
                      onClick={handleClickNote}
                    >
                      {lineItem?.label}
                    </DetailTitleButton>
                  ) : (
                    <DetailTitle data-qa={getDataQaLabel(lineItem?.type)}>
                      {lineItem?.label}
                    </DetailTitle>
                  )}
                  <DetailValue
                    data-qa={getDataQaAmount(lineItem?.type)}
                    $hasNumber={/\d/.test(lineItem?.formattedAmount)}
                    $type={lineItem?.type}
                  >
                    {lineItem?.formattedAmount}
                  </DetailValue>
                </PriceLine>
                {lineItem?.type === PriceBreakdownLineItemType.SUBTOTAL &&
                  orderSubtotal?.subItems?.length > 0 && (
                    <>
                      {orderSubtotal?.subItems?.map((subItem) => {
                        if (subItem?.type === PriceBreakdownLineItemType.SUBTOTAL_TOTAL_PRICE) {
                          return null
                        }
                        return (
                          <PriceLine
                            $isSubtotalBreakdownStart={
                              subItem?.type === PriceBreakdownLineItemType.PRODUCT_PRICE
                            }
                            key={subItem?.type}
                          >
                            <DetailTitle data-qa={getDataQaLabel(subItem?.type)}>
                              {subItem?.label}
                            </DetailTitle>
                            <DetailValue data-qa={getDataQaAmount(subItem?.type)}>
                              {subItem?.formattedAmount}
                            </DetailValue>
                          </PriceLine>
                        )
                      })}
                      <Divider />
                    </>
                  )}
              </>
            )
          )
        })}
      </StickyOrderSummaryContainer>

      <ApplicableCostsModal
        body={order?.applicableCostsNote}
        isOpen={isApplicableCostsModalOpen}
        onClose={handleCloseModal}
      />
    </Container>
  )
}

const getDataQaAmount = (type: string) => {
  switch (type) {
    case PriceBreakdownLineItemType.CREDIT:
      return CheckoutQA.OrderSummaryCreditAmount
    case PriceBreakdownLineItemType.PROMOTION:
      return CheckoutQA.OrderSummarySubtotalAmount
    case PriceBreakdownLineItemType.SHIPPING:
      return CheckoutQA.OrderSummaryShippingAmount
    case PriceBreakdownLineItemType.SUBTOTAL:
      return CheckoutQA.OrderSummarySubtotalAmount
    case PriceBreakdownLineItemType.INCLUDED_TAX_AND_DUTY:
    case PriceBreakdownLineItemType.TAX:
      return CheckoutQA.OrderSummaryTaxAmount
    default:
      return ''
  }
}

const getDataQaLabel = (type: string) => {
  switch (type) {
    case PriceBreakdownLineItemType.CREDIT:
      return CheckoutQA.OrderSummaryCreditTag
    case PriceBreakdownLineItemType.PROMOTION:
      return CheckoutQA.OrderSummarySubtotalTag
    case PriceBreakdownLineItemType.SHIPPING:
      return CheckoutQA.OrderSummaryShippingTag
    case PriceBreakdownLineItemType.SUBTOTAL:
      return CheckoutQA.OrderSummarySubtotalTag
    case PriceBreakdownLineItemType.INCLUDED_TAX_AND_DUTY:
    case PriceBreakdownLineItemType.TAX:
      return CheckoutQA.OrderSummaryTaxTag
    default:
      return ''
  }
}

const Container = styled.div<{ $displayLineItems: boolean }>`
  ${fonts.BODY_TEXT};
  background-color: ${colors.FC2_WHITE};
  margin-top: 15px;
  min-height: fit-content;
  padding: 20px;
  position: relative;
  width: 100%;
`
const Option = styled(RadioButton)`
  height: 92px;
  margin-left: 6px;
  margin-right: 6px;
  padding: 15px;
  width: 50%;

  ${RadioButton.Subtitle} {
    font-size: 12px;
    font-weight: 400;
    line-height: 12px;
    margin-top: 36px;
    text-align: left;
  }
`
const RadioOptions = styled(RadioGroup)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 30px;
  width: 100%;

  ${/* sc-selector */ Option}:first-child {
    margin-left: 0px;
  }
  ${/* sc-selector */ Option}:last-child {
    margin-right: 0px;
  }
`
const MarginDivider = styled(Divider)`
  margin-bottom: 0px;
  margin-top: 30px;
`
const Flex = styled.div`
  display: flex;
`
const OrderSummaryCopy = styled.p`
  margin: 0;
`
const ProductSummaryContainer = styled(Flex)`
  align-items: center;
`
const ProductDetails = styled(OrderSummaryCopy)`
  ${fonts.BODY_TEXT}
`
const ProductDescription = styled.div`
  ${fonts.BODY_TEXT}
  flex-grow: 1;
`
const Size = styled(OrderSummaryCopy)`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_GREY};
`
const ProductDetailsLineTwo = styled(Flex)`
  justify-content: space-between;
  margin-top: 5px;
`
const ProductName = styled(OrderSummaryCopy)`
  ${fonts.SUBTITLE_2}
  font-weight: bold;
  margin: 5px 0;
`
const StyledImage = styled(Image)<{ src: string }>`
  background-image: url(${({ src }) => src});
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  margin-right: 12px;
  max-width: 77px;
  min-height: 77px;
  object-fit: contain;
  width: 100%;
`
const LineItem = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`
const LineItemText = styled.div`
  color: ${colors.FC2_BLACK};
  font-weight: 500;
  padding-right: 10px;
`
const LineItemValue = styled.div`
  color: ${colors.FC2_BLACK};
  font-weight: 500;
`
const Caret = styled(RotatingCaret)`
  height: 6px;
  margin-bottom: 1px;
  margin-left: 8px;
  margin-top: 2px;
  width: 9px;
`
const DetailTitle = styled.div`
  color: ${colors.FC2_GREY};
  margin-right: 5px;
`
const DetailTitleButton = styled.button`
  color: ${colors.FC2_GREY};
  margin-right: 5px;
  text-decoration: underline;
`
const DetailValue = styled.div<{ $hasNumber?: boolean; $type?: string }>`
  ${({ $hasNumber }) => !$hasNumber && 'font-weight: normal;'};
  ${fonts.SUBTITLE_3}
  color: ${({ $type }) =>
    $type === PriceBreakdownLineItemType.CREDIT || $type === PriceBreakdownLineItemType.PROMOTION
      ? `${colors.FC2_RED}`
      : `${colors.FC2_BLACK}`};
  margin: 0;
`
const PriceLine = styled.div<{ $isSubtotal?: boolean; $isSubtotalBreakdownStart?: boolean }>`
  align-items: flex-end;
  color: ${colors.FC2_DARK_GREY};
  display: flex;
  justify-content: space-between;
  margin-top: ${({ $isSubtotal, $isSubtotalBreakdownStart }) => {
    if ($isSubtotal) return '30px'
    if ($isSubtotalBreakdownStart) return '20px'
    return '10px'
  }};
`
const StickyOrderSummaryObserver = styled.div`
  height: 20px;
`
const StickyOrderSummaryContainer = styled.div`
  background-color: ${colors.FC2_WHITE};

  &.sticky-summary {
    border-bottom: 1px solid ${colors.FC2_LIGHTEST_GREY};
    box-shadow: 0px 4px 8px 0px #00000014;
    left: 0;
    padding: 20px;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 1001;
  }
`
